import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

// CharAutocomplete — screenplay character-name suggester. Watches the word at
// the caret; when it's ≥ minLength letters and lives at a word boundary (start
// of paragraph or after whitespace), it asks the host for matching known
// character names and opens a popup. Accepting an entry replaces the typed
// prefix with a `<char>NAME</char>` mark + a trailing space, then drops the
// mark so the next keystroke escapes the wrapper.
//
// Unlike SlashCommand this does not pipe through `@tiptap/suggestion` — that
// plugin requires a trigger character. We need to trigger on any word start,
// so we run a small custom ProseMirror plugin that walks the text before the
// caret on every transaction.
const charAutoKey = new PluginKey('charAutocomplete');

// Word at the caret: a run of letters / apostrophes / hyphens immediately
// before the cursor, anchored at a word boundary (paragraph start or
// whitespace). Returns null when there's no qualifying word.
function wordBeforeCaret(state) {
  const sel = state.selection;
  if (!sel.empty) return null;
  const { $from } = sel;
  // Don't trigger inside an existing character mark — the writer is already
  // editing a wrapped name, not typing a fresh prefix.
  const charMark = state.schema.marks.char;
  if (charMark && charMark.isInSet($from.marks())) return null;

  const parentOffset = $from.parentOffset;
  const textBefore = $from.parent.textBetween(0, parentOffset, '\ufffc', '\ufffc');
  const m = /([\p{L}][\p{L}'-]*)$/u.exec(textBefore);
  if (!m) return null;
  const word = m[1];
  const wordStart = parentOffset - word.length;
  if (wordStart > 0) {
    const prev = textBefore[wordStart - 1];
    if (!/\s/.test(prev)) return null;
  }
  return {
    from: $from.start() + wordStart,
    to: $from.start() + parentOffset,
    query: word,
  };
}

const CharAutocomplete = Extension.create({
  name: 'charAutocomplete',

  addOptions() {
    return {
      minLength: 2,
      // ({ query }) => Array<{ name: string, label: string }>
      getItems: () => [],
      // ({ items, clientRect, command, query }) => void
      onStart: () => {},
      onUpdate: () => {},
      onExit: () => {},
      // (event) => boolean — return true to consume the key.
      onKeyDown: () => false,
      // Optional host override for accept. When provided, replaces the default
      // command-builder. Receives ({ editor, item, query, range }) and is
      // responsible for the full insert chain (delete range + insert text +
      // unset mark + trailing space).
      onAccept: null,
    };
  },

  addProseMirrorPlugins() {
    const { options } = this;
    const { editor } = this;

    let active = null;
    const close = () => {
      if (!active) return;
      active = null;
      options.onExit?.();
    };

    return [
      new Plugin({
        key: charAutoKey,
        view: (view) => ({
          update: (v) => {
            const match = wordBeforeCaret(v.state);
            if (!match || match.query.length < options.minLength) {
              close();
              return;
            }
            if (match.query.startsWith('/')) {
              close();
              return;
            }
            const items = options.getItems({ query: match.query }) || [];
            if (items.length === 0) {
              close();
              return;
            }
            const command = (item) => {
              if (!item) return;
              if (typeof options.onAccept === 'function') {
                options.onAccept({
                  editor,
                  item,
                  query: match.query,
                  range: { from: match.from, to: match.to },
                });
                return;
              }
              editor
                .chain()
                .focus()
                .deleteRange({ from: match.from, to: match.to })
                .insertContent([
                  { type: 'text', text: item.name, marks: [{ type: 'char' }] },
                  { type: 'text', text: ' ' },
                ])
                .unsetMark('char')
                .run();
            };
            const clientRect = () => {
              try {
                const coords = v.coordsAtPos(match.to);
                return {
                  left: coords.left,
                  right: coords.right,
                  top: coords.top,
                  bottom: coords.bottom,
                  width: 0,
                  height: coords.bottom - coords.top,
                };
              } catch (_e) {
                return null;
              }
            };
            const payload = { items, clientRect, command, query: match.query };
            if (active) {
              active = { ...match, items, command };
              options.onUpdate?.(payload);
            } else {
              active = { ...match, items, command };
              options.onStart?.(payload);
            }
          },
          destroy: () => close(),
        }),
        props: {
          handleKeyDown(_view, event) {
            if (!active) return false;
            // Space — if the typed word matches an item exactly
            // (case-insensitive), auto-wrap it. Otherwise let the space pass
            // and close the menu so it doesn't linger over the next word.
            if (event.key === ' ' || event.code === 'Space') {
              const q = (active.query || '').toUpperCase();
              const exact = (active.items || []).find(
                (it) => String(it.name).toUpperCase() === q,
              );
              if (exact) {
                event.preventDefault();
                active.command?.(exact);
                close();
                return true;
              }
              close();
              return false;
            }
            const handled = options.onKeyDown?.(event);
            return Boolean(handled);
          },
        },
      }),
    ];
  },
});

export default CharAutocomplete;
